<div class="push-destination-dialog">
  <!-- Dialog Header -->
  <div class="dialog-header">
    <h1 class="dialog-title">{{ dialogTitle }}</h1>
    <button
      mat-icon-button
      class="close-button"
      (click)="onCancel()"
      aria-label="Close"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Dialog Content -->
  <mat-dialog-content class="dialog-content">
    <form [formGroup]="form" (ngSubmit)="onSave()">
      <!-- Service Type Selection -->
      <div class="form-section">
        <span class="section-label">Service Type</span>
        <div
          class="service-type-cards"
          role="radiogroup"
          aria-label="Service Type"
        >
          @for (st of availableServiceTypes; track st.value) {
            <div
              class="service-card"
              [class.selected]="selectedServiceType === st.value"
              [class.disabled]="isEditMode && selectedServiceType !== st.value"
              role="radio"
              [attr.aria-checked]="selectedServiceType === st.value"
              [attr.tabindex]="
                isEditMode && selectedServiceType !== st.value ? -1 : 0
              "
              (click)="
                !isEditMode && form.get('serviceType')?.setValue(st.value)
              "
              (keydown.enter)="
                !isEditMode && form.get('serviceType')?.setValue(st.value)
              "
              (keydown.space)="
                !isEditMode && form.get('serviceType')?.setValue(st.value);
                $event.preventDefault()
              "
            >
              <mat-icon class="service-card-icon service-{{ st.value }}">{{
                st.icon
              }}</mat-icon>
              <span class="service-card-label">{{ st.label }}</span>
            </div>
          }
        </div>
      </div>

      <!-- Name -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Name</mat-label>
        <input
          matInput
          formControlName="name"
          placeholder="e.g., Slack #deploy-notifications"
          autocomplete="off"
          spellcheck="false"
        />
        @if (form.get("name")?.hasError("required")) {
          <mat-error>Name is required</mat-error>
        }
      </mat-form-field>

      <!-- Webhook URL (non-email service types) -->
      @if (!isEmail) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Webhook URL</mat-label>
          <input
            matInput
            formControlName="url"
            placeholder="https://..."
            autocomplete="off"
            spellcheck="false"
          />
          <mat-hint>{{ urlHint }}</mat-hint>
          @if (form.get("url")?.hasError("required")) {
            <mat-error>URL is required</mat-error>
          }
          @if (form.get("url")?.hasError("httpsRequired")) {
            <mat-error>URL must use HTTPS</mat-error>
          }
          @if (form.get("url")?.hasError("invalidUrl")) {
            <mat-error>Enter a valid URL</mat-error>
          }
        </mat-form-field>
      }

      <!-- Email recipients (email service type) -->
      @if (isEmail) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Recipients</mat-label>
          <input
            matInput
            formControlName="emailTo"
            placeholder="ops@example.com, manager@example.com"
            autocomplete="off"
            spellcheck="false"
          />
          <mat-hint
            >Comma- or space-separated email addresses. Sent from the
            deployment's configured address.</mat-hint
          >
          @if (form.get("emailTo")?.hasError("required")) {
            <mat-error>At least one recipient is required</mat-error>
          }
          @if (form.get("emailTo")?.hasError("invalidEmail")) {
            <mat-error>Enter valid email addresses</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Reply-To (optional)</mat-label>
          <input
            matInput
            formControlName="emailReplyTo"
            placeholder="team-inbox@example.com"
            autocomplete="off"
            spellcheck="false"
          />
          <mat-hint>Where replies should go, e.g. a team inbox.</mat-hint>
        </mat-form-field>
      }

      <!-- Scope -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Scope</mat-label>
        <mat-select formControlName="scope">
          <mat-option value="project"
            >Project (visible to all project members)</mat-option
          >
          <mat-option value="personal">Personal (only you)</mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Events -->
      <div class="form-section">
        <span class="section-label" id="events-label">Events</span>
        <p class="section-hint">
          Select which events trigger a notification to this destination.
        </p>
        <div class="event-checkboxes">
          @for (et of eventTypes; track et.value) {
            <mat-checkbox
              [formControlName]="'event_' + et.value"
              color="primary"
            >
              {{ et.label }}
            </mat-checkbox>
          }
        </div>
        @if (selectedEvents.length === 0 && form.touched) {
          <p class="events-error">Select at least one event</p>
        }
      </div>

      <!-- Signing Secret (Generic only) -->
      @if (isGeneric) {
        <div class="form-section credential-section">
          <span class="section-label">Signing Secret (optional)</span>
          <div class="credential-info">
            <mat-icon>info_outline</mat-icon>
            <span
              >If provided, notifications will include an HMAC signature header
              for verification.</span
            >
          </div>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Signing Secret</mat-label>
            <input
              matInput
              formControlName="credential"
              [type]="showCredential() ? 'text' : 'password'"
              placeholder="Optional signing secret"
              autocomplete="off"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              (click)="toggleCredentialVisibility()"
              [attr.aria-label]="showCredential() ? 'Hide secret' : 'Show secret'"
            >
              <mat-icon>{{
                showCredential() ? "visibility_off" : "visibility"
              }}</mat-icon>
            </button>
          </mat-form-field>
        </div>
      }

      <!-- Enabled Toggle (edit mode only) -->
      @if (isEditMode) {
        <div class="form-section">
          <mat-slide-toggle formControlName="enabled" color="primary">
            Enabled
          </mat-slide-toggle>
        </div>
      }

      <!-- Error Display -->
      @if (error()) {
        <div class="error-banner">
          <mat-icon>error_outline</mat-icon>
          <span>{{ error() }}</span>
        </div>
      }

      <!-- Dialog Actions -->
      <mat-dialog-actions align="end" class="dialog-actions">
        <button mat-stroked-button type="button" (click)="onCancel()">
          Cancel
        </button>
        <button
          mat-flat-button
          color="primary"
          type="submit"
          [disabled]="!canSave"
        >
          @if (isSaving()) {
            <mat-spinner diameter="18" class="save-spinner"></mat-spinner>
          }
          {{ saveButtonText }}
        </button>
      </mat-dialog-actions>
    </form>
  </mat-dialog-content>
</div>
